import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PlayerStatSheetView from './PlayerStatSheetView';
import { PlayerSheetTarget } from './playerSheetTypes';
import { usePlayerSheetData } from './usePlayerSheetData';

interface PlayerSheetContextValue {
  openSheet: (target: PlayerSheetTarget) => void;
  closeSheet: () => void;
  isOpen: boolean;
}

const PlayerSheetContext = createContext<PlayerSheetContextValue | null>(null);

export const usePlayerSheet = () => {
  const ctx = useContext(PlayerSheetContext);
  if (!ctx) {
    throw new Error('usePlayerSheet must be used within PlayerSheetProvider');
  }
  return ctx;
};

export const usePlayerSheetOptional = () => useContext(PlayerSheetContext);

const PlayerSheetDrawer: React.FC<{
  target: PlayerSheetTarget;
  onClose: () => void;
}> = ({ target, onClose }) => {
  const { data, loading, error } = usePlayerSheetData(target);
  const clientId = target.clientId;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[80]">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close player stat sheet"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Player stat sheet"
        className="absolute inset-y-0 right-0 w-full max-w-2xl bg-[#f8fbfe] shadow-2xl flex flex-col animate-[slideIn_200ms_ease-out]"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#bccedf] bg-white">
          <h2 className="text-base font-bold text-[#05396b]">Player Stat Sheet</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full text-2xl leading-none text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && (
            <div className="py-16 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#05396b]" />
            </div>
          )}
          {!loading && (error || !data) && (
            <div className="bg-white border border-[#bccedf] rounded-lg p-6 text-center text-red-600">
              {error || 'Player not found'}
            </div>
          )}
          {!loading && data && (
            <PlayerStatSheetView
              data={data}
              variant={target.variant || 'season'}
              clientId={clientId || data.client?._id}
              initialSeasonId={target.seasonId}
            />
          )}
        </div>
      </aside>
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>,
    document.body,
  );
};

export const PlayerSheetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [target, setTarget] = useState<PlayerSheetTarget | null>(null);
  const openSheet = useCallback((next: PlayerSheetTarget) => setTarget(next), []);
  const closeSheet = useCallback(() => setTarget(null), []);
  const value = useMemo(
    () => ({ openSheet, closeSheet, isOpen: !!target }),
    [openSheet, closeSheet, target],
  );

  return (
    <PlayerSheetContext.Provider value={value}>
      {children}
      {target && <PlayerSheetDrawer target={target} onClose={closeSheet} />}
    </PlayerSheetContext.Provider>
  );
};
